import { InboxOutlined } from "@ant-design/icons";
import {
  ArrowTopRightOnSquareIcon,
  TrashIcon,
} from "@heroicons/react/16/solid";
import {
  ArrowUturnLeftIcon,
  ClipboardDocumentIcon,
  PlusIcon,
  ShoppingCartIcon,
} from "@heroicons/react/20/solid";
import Dragger from "antd/es/upload/Dragger";
import { clsx } from "clsx";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  LimitedTimeOfferExpirationTypeEnum,
  WhatsappTemplateCategoryEnum,
} from "../enums";
import formChecks from "../functions/templateFormVerification";
import { deleteEdge } from "../functions/deleteEdge";
import { handleBeforeUploadFile } from "../functions/handleBeforeUploadFile";
import { handleUploadFile } from "../functions/handleUploadFile";
import { CustomerContext } from "../providers";
import { CarouselForm } from "./carousel/CarouselForm";
import { handleMessageChange } from "./handleMessageChange";
import { handleUpdateCardComponent } from "./handleUpdateCardComponent";
import { LimitedTimeOffer } from "./limited-time-offer/LimitedTimeOffer";
import { Button } from "./tailwind/button";
import { Divider } from "./tailwind/divider";
import {
  Description,
  ErrorMessage,
  Field,
  Fieldset,
  Label,
  Legend,
} from "./tailwind/fieldset";
import { Input, InputGroup } from "./tailwind/input";
import { Radio, RadioField, RadioGroup } from "./tailwind/radio";
import { Select } from "./tailwind/select";
import SelectInput from "./tailwind/select-input";
import { Switch, SwitchOnOff } from "./tailwind/switch";
import { Text } from "./tailwind/text";
import { Textarea } from "./tailwind/textarea";
import "./template-form.css";
import { TemplatePreview } from "./TemplatePreview";
import { Tooltip } from "./Tooltip";

export default function TemplateForm({
  api,
  user,
  edges,
  setEdges,
  templateName,
  setTemplateName,
  selectedCategory,
  setSelectedCategory,
  selectedHeader,
  setSelectedHeader,
  selectedCardsHeader,
  setSelectedCardsHeader,
  isLimitedTimeOffer,
  setIsLimitedTimeOffer,
  limitedTimeOffer,
  setLimitedTimeOffer,
  cards,
  setCards,
  body,
  setBody,
  footer,
  setFooter,
  numberOfQuickReplies,
  setNumberOfQuickReplies,
  flowButton,
  copyCodeButton,
  setCopyCodeButton,
  quickReply1,
  setQuickReply1,
  quickReply2,
  setQuickReply2,
  quickReply3,
  setQuickReply3,
  urlButton,
  urlButton2,
  numberOfUrl,
  setNumberOfUrl,
  setUrlButton,
  urlButtonLink,
  setUrlButtonLink,
  setUrlButton2,
  urlButtonLink2,
  setUrlButtonLink2,
  isUrlButtonAtBottom,
  setIsUrlButtonAtBottom,
  headerImageLink,
  setHeaderImageLink,
  headerVideoLink,
  setHeaderVideoLink,
  templateNameRef,
  bodyRef,
  quickReply1Ref,
  quickReply2Ref,
  quickReply3Ref,
  urlButtonLinkRef,
  urlButtonLink2Ref,
  bodyCardRefs,
  buttonCardRefs,
  isInPanel,
  nodes,
  setNodes,
  selectedNode,
  imageParamOptions,
  paramOptions,
  campaign,
  isUploading,
  setIsUploading,
  setModifications,
  handleOpenProductPicker,
  shopifyUpsellLineItems,
  setIsPanelOpened,
  setFile,
  carouselCardIndex,
  setCarouselCardIndex,
  carouselComponentIndex,
  setCarouselComponentIndex,
  selectedNodeConvertCompress,
  setSelectedNodeConvertCompress,
  setIsConvertCompressDialogOpen,
  templateNameError,
  setTemplateNameError,
  bodyErrorMsg,
  setBodyErrorMsg,
  quickReply1ErrorMsg,
  setQuickReply1ErrorMsg,
  quickReply2ErrorMsg,
  setQuickReply2ErrorMsg,
  quickReply3ErrorMsg,
  setQuickReply3ErrorMsg,
  urlButtonErrorMsg,
  setUrlButtonErrorMsg,
  urlButtonLinkErrorMsg,
  setUrlButtonLinkErrorMsg,
  urlButton2ErrorMsg,
  setUrlButton2ErrorMsg,
  urlButtonLink2ErrorMsg,
  setUrlButtonLink2ErrorMsg,
  limitedTimeOfferTextError,
  setLimitedTimeOfferTextError,
  customField1ErrorMsg,
  setCustomField1ErrorMsg,
  customField2ErrorMsg,
  setCustomField2ErrorMsg,
  customField3ErrorMsg,
  setCustomField3ErrorMsg,
  bodyCardErrors,
  setBodyCardErrors,
  buttonCardErrors,
  setButtonCardErrors,
  replyButtons,
  setReplyButtons,
}) {
  const { shop, integrations } = useContext(CustomerContext);

  const customFieldOptions = useMemo(() => {
    if (!user?.customer?.customFields) return [];
    return Object.keys(user.customer.customFields)
      .filter(Boolean)
      .map((fieldKey) => ({
        label: fieldKey,
        value: fieldKey,
      }));
  }, [user?.customer?.customFields]);

  const cleanTemplateName = (name) =>
    removeEmojisFromText(
      name
        .toLowerCase()
        .replaceAll(" ", "_")
        .replaceAll("-", "_")
        .replaceAll("%", "")
        .replaceAll(",", "")
        .replaceAll(";", "")
        .replaceAll("!", "")
        .replaceAll("?", "")
        .replaceAll("[", "")
        .replaceAll("]", "")
        .replaceAll("'", "")
        .replaceAll(".", "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, ""),
    );

  const removeEmojisFromText = (text) =>
    text.replace(
      /([\u2700-\u27BF]|[\uE000-\uF8FF]|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|[\u2011-\u26FF]|\uD83E[\uDD10-\uDDFF])/g,
      "",
    );

  const handleCategoryChange = useCallback((value) => {
    setSelectedCategory(value);
    const _isLimitedTimeOffer =
      value === WhatsappTemplateCategoryEnum.LIMITED_TIME_OFFER;
    const _limitedTimeOffer = _isLimitedTimeOffer
      ? { text: "", expirationType: LimitedTimeOfferExpirationTypeEnum.DELAY }
      : null;
    const _isCarousel = value === WhatsappTemplateCategoryEnum.CAROUSEL;
    const _cards = [
      {
        components: [
          {
            link: "",
            type: "HEADER",
            format: "IMAGE",
          },
          {
            text: "",
            type: "BODY",
            bodyParameters: [],
          },
          {
            type: "BUTTONS",
            buttons: [
              {
                text: "",
                type: "QUICK_REPLY",
              },
            ],
          },
        ],
      },
      {
        components: [
          {
            link: "",
            type: "HEADER",
            format: "IMAGE",
          },
          {
            text: "",
            type: "BODY",
            bodyParameters: [],
          },
          {
            type: "BUTTONS",
            buttons: [
              {
                text: "",
                type: "QUICK_REPLY",
              },
            ],
          },
        ],
      },
    ];
    if (_isLimitedTimeOffer) {
      setFooter("");
      setUrlButton2(null);
      setUrlButtonLink2(null);
      if (numberOfUrl !== 1) {
        setNumberOfUrl(1);
      }
    } else if (_isCarousel) {
      setFooter("");
      setSelectedCardsHeader(["image"]);
      setNumberOfUrl(0);
      setNumberOfQuickReplies(0);
      setQuickReply1(null);
      setQuickReply2(null);
      setQuickReply3(null);
      setLimitedTimeOffer(null);
      setUrlButton(null);
      setUrlButton2(null);
      setSelectedHeader(["none"]);
    } else {
      setIsLimitedTimeOffer(null);
      setLimitedTimeOffer(null);
      if (isInPanel) {
        setCards(null);
      }
    }

    setIsLimitedTimeOffer(_isLimitedTimeOffer);
    setLimitedTimeOffer(_limitedTimeOffer);
    if (isInPanel) {
      setCards([..._cards]);
    }
  }, []);

  const handleHeaderChange = useCallback(
    (value) => {
      setSelectedHeader([value]);

      if (isInPanel) {
        setModifications(true);
        if (value === "image") {
          const headerParameters = {
            type: "image",
            image: {
              link: "https://firebasestorage.googleapis.com/v0/b/phocea-75b47.appspot.com/o/Compa%2Fph_image.png?alt=media&token=b42e2452-09c4-4076-9956-15520363da4b",
            },
          };
          handleMessageChange(
            "headerParameters",
            headerParameters,
            nodes,
            setNodes,
            selectedNode,
          );
        }
        if (value === "video") {
          const headerParameters = {
            type: "video",
            video: {
              link: "https://storage.gadget.dev/files/22129/42940/media/file/xDycluQ_j76bA150Asp7G/2bc118d2-9e93-405c-9b5e-dced1b1051c0",
            },
          };
          handleMessageChange(
            "headerParameters",
            headerParameters,
            nodes,
            setNodes,
            selectedNode,
          );
        }
        if (value === "none") {
          const headerParameters = {};
          handleMessageChange(
            "headerParameters",
            headerParameters,
            nodes,
            setNodes,
            selectedNode,
          );
        }
      }
    },
    [selectedHeader],
  );

  const handleLimitedTimeOfferChange = (value) => {
    setLimitedTimeOffer(value);
    if (isInPanel) {
      handleMessageChange(
        "limitedTimeOffer",
        value,
        nodes,
        setNodes,
        selectedNode,
      );
    }
  };

  const handleFooterChange = useCallback((e) => {
    setFooter(e.target.value);
    if (isInPanel) {
      setModifications(true);
      handleMessageChange(
        "footer",
        e.target.value,
        nodes,
        setNodes,
        selectedNode,
      );
    }
  }, []);

  let previewClasses = "";
  let previewMargin = "";
  let nameClasses = "";
  if (isInPanel) {
    previewClasses =
      "fixed w-96 top-1/2 right-1/2 transform -translate-x-[63%] -translate-y-1/2 bg-white overflow-y-auto rounded-customBorderRadius max-h-[90vh] scroll-color ";
    previewMargin = "m-3";
    nameClasses = "";
  } else {
    previewClasses = "sticky hidden w-96 top-1 sm:block";
    previewMargin = "ml-[5em] mb-[20px]";
    nameClasses = "mx-auto flex w-full max-w-7xl items-start pb-5 gap-x-8";
  }

  const categoriesOptions = [
    { label: "Utility", value: WhatsappTemplateCategoryEnum.UTILITY },
    { label: "Marketing", value: WhatsappTemplateCategoryEnum.MARKETING },
    {
      label: "Limited-Time Offer",
      value: WhatsappTemplateCategoryEnum.LIMITED_TIME_OFFER,
    },
    ...(isInPanel
      ? [{ label: "Carousel", value: WhatsappTemplateCategoryEnum.CAROUSEL }]
      : []),
  ];

  const handleCopyCodeButtonChange = (e) => {
    const text = e.target.value.replace(/[^a-zA-Z0-9]/g, "");
    const _copyCodeButton = { text };
    setCopyCodeButton(_copyCodeButton);
    if (isInPanel) {
      setModifications(true);
      handleMessageChange(
        "copyCodeButton",
        _copyCodeButton,
        nodes,
        setNodes,
        selectedNode,
      );
    }
  };

  const updateReplyButton = (index, title) => {
    const updatedButtons = replyButtons ? [...replyButtons] : [];
    updatedButtons[index] = {
      ...updatedButtons[index],
      title,
    };
    setReplyButtons(updatedButtons);
    if (isInPanel) {
      handleMessageChange(
        "buttons",
        updatedButtons,
        nodes,
        setNodes,
        selectedNode,
      );
    }

    if (index === 0) {
      const edgesToUpdate = edges.filter(
        (edge) =>
          edge.source === selectedNode.id &&
          edge.sourceHandle !== "main" &&
          edge.sourceHandle !== "" &&
          edge.sourceHandle !== "urlA" &&
          edge.sourceHandle !== "urlB",
      );

      if (edgesToUpdate.length > 0) {
        const edgeToModify = edgesToUpdate[0];
        const oldEdgeId = edgeToModify.id;
        const newEdgeId = `${selectedNode.id}a->${edgeToModify.target}`;

        if (edgeToModify) {
          edgeToModify.id = newEdgeId;
          if (
            edgeToModify.sourceHandle !== "urlA" &&
            edgeToModify.sourceHandle !== "urlB"
          )
            edgeToModify.sourceHandle = "a";

          const updatedEdges = edges.map((edge) =>
            edge.id === oldEdgeId ? edgeToModify : edge,
          );

          setEdges(updatedEdges);
        }
      }
    }
  };

  const removeEdgeOnUrlButton = async (index) => {
    if (index === 0) {
      const edgeToDelete = edges.find(
        (e) => e.source === selectedNode.id && e.sourceHandle === "urlA",
      );
      if (edgeToDelete) {
        await deleteEdge(edgeToDelete, setEdges, api);
      }
    } else if (index === 1) {
      const edgeToDelete = edges.find(
        (e) => e.source === selectedNode.id && e.sourceHandle === "urlB",
      );
      if (edgeToDelete) {
        await deleteEdge(edgeToDelete, setEdges, api);
      }
    }
  };

  const removeReplyButton = async (index) => {
    const updatedButtons = [...replyButtons];
    updatedButtons.splice(index, 1);
    setReplyButtons(updatedButtons);
    if (isInPanel) {
      handleMessageChange(
        "buttons",
        updatedButtons,
        nodes,
        setNodes,
        selectedNode,
      );
    }

    const baseId = selectedNode.id;
    const sourceHandle = String.fromCharCode(97 + index);
    const sourceId = baseId;

    if (index === 0) {
      const edgesToKeep = edges.filter(
        (edge) =>
          edge.source === selectedNode.id &&
          edge.sourceHandle !== "main" &&
          edge.sourceHandle !== "" &&
          edge.sourceHandle !== "urlA" &&
          edge.sourceHandle !== "urlB",
      );
      const edgeToModify = edgesToKeep[0];
      const existingEdgeToMain = edges.filter(
        (edge) =>
          (edge.source === selectedNode.id && edge.sourceHandle === "main") ||
          (edge.source === selectedNode.id && edge.sourceHandle === ""),
      );

      if (edgeToModify && existingEdgeToMain.length === 0) {
        const oldEdgeId = edgeToModify.id;
        const newEdgeId = `${selectedNode.id}->${edgeToModify.target}`;
        edgeToModify.id = newEdgeId;
        edgeToModify.sourceHandle = "main";
        edgeToModify.source = selectedNode.id;

        const updatedEdges = edges.map((edge) =>
          edge.id === oldEdgeId ? edgeToModify : edge,
        );

        setEdges(updatedEdges);
      } else if (edgeToModify && existingEdgeToMain) {
        await deleteEdge(edgeToModify, setEdges, api);
      }
    } else {
      const edgeToDelete = edges.find(
        (edge) =>
          edge.source === sourceId && edge.sourceHandle === sourceHandle,
      );
      await deleteEdge(edgeToDelete, setEdges, api);
    }
  };

  const [newBodyParameters, setNewBodyParameters] = useState(
    selectedNode?.data?.bodyParameters ?? [],
  );

  const [parameterToInsert, setParameterToInsert] = useState("");

  const urlVariableNotAtEnd1 = useMemo(() => {
    if (!urlButtonLink) {
      return false;
    }
    if (
      urlButtonLink.includes("{{url_variable}}") &&
      !urlButtonLink.endsWith("{{url_variable}}")
    ) {
      return true;
    }
    if (urlButtonLink.includes("{{1}}") && !urlButtonLink.endsWith("{{1}}")) {
      return true;
    }
    return false;
  }, [urlButtonLink]);

  const urlVariableNotAtEnd2 = useMemo(() => {
    if (!urlButtonLink2) {
      return false;
    }
    if (
      urlButtonLink2.includes("{{url_variable}}") &&
      !urlButtonLink2.endsWith("{{url_variable}}")
    ) {
      return true;
    }
    if (urlButtonLink2.includes("{{1}}") && !urlButtonLink2.endsWith("{{1}}")) {
      return true;
    }
    return false;
  }, [urlButtonLink2]);

  const handleBodyChange = useCallback(
    (e) => {
      const updatedValue = e.target.value;
      setBody(updatedValue);

      if (isInPanel) {
        setModifications(true);
        updateBodyParameters(updatedValue, null);
        handleMessageChange(
          "body",
          updatedValue,
          nodes,
          setNodes,
          selectedNode,
        );
      }
    },
    [isInPanel, nodes, selectedNode, setBody, setNodes],
  );

  const updateBodyParameters = (currentBody, parameter) => {
    let k = 1;
    var updatedParams = [...(selectedNode?.data?.bodyParameters ?? [])];

    // Ajoute les paramètres présents dans le texte body
    while (currentBody.includes(`{{${k}}}`)) {
      if (k > (updatedParams?.length ?? 0)) {
        parameter
          ? updatedParams.push({
              selected: parameter,
              type: "text",
            })
          : updatedParams.push({
              selected: "contact.whatsappName",
              type: "text",
            });
      }
      k++;
    }
    updatedParams = updatedParams?.slice(0, k - 1);
    setNewBodyParameters(updatedParams);

    handleMessageChange(
      "bodyParameters",
      updatedParams,
      nodes,
      setNodes,
      selectedNode,
    );
  };

  const updateBodyCardParameters = (currentBody, cardIndex, componentIndex) => {
    const currentCard = selectedNode?.data?.cards[cardIndex];
    const currentBodyParams =
      currentCard.components[componentIndex].bodyParameters || [];
    let updatedParams = [...currentBodyParams];

    let k = 1;
    while (currentBody.includes(`{{${k}}}`)) {
      if (k > updatedParams.length) {
        updatedParams.push({
          type: "text",
          selected: parameterToInsert || "contact.whatsappName",
        });
      }
      k++;
    }
    updatedParams = updatedParams.slice(0, k - 1);

    handleUpdateCardComponent(
      selectedNode,
      cardIndex,
      componentIndex,
      { bodyParameters: updatedParams },
      nodes,
      setNodes,
      setCards,
    );
  };

  const addVariable = (isIncard, cardIndex, componentIndex) => {
    const textarea = isIncard
      ? bodyCardRefs.current[cardIndex]
      : bodyRef.current;
    const textBefore = textarea.value.substring(0, textarea.selectionStart);
    const textAfter = textarea.value.substring(textarea.selectionEnd);
    let newCursorPosition;
    let updatedValue;

    if (!isIncard) {
      const existingParamIndex = selectedNode.data.bodyParameters
        ? selectedNode.data.bodyParameters.findIndex(
            (param) => param.selected === parameterToInsert,
          ) + 1
        : 1;

      const insertionText = `{{${
        existingParamIndex || selectedNode.data.bodyParameters.length + 1
      }}}`;
      updatedValue = `${textBefore}${insertionText}${textAfter}`;
      textarea.value = updatedValue;
      handleMessageChange("body", textarea.value, nodes, setNodes);
      setBody(textarea.value);
      newCursorPosition = textBefore.length + insertionText.length;
    } else {
      const cardBodyParams = selectedNode?.data?.cards[
        cardIndex
      ]?.components.find((c) => c.type === "BODY")?.bodyParameters;
      const existingParamIndex = cardBodyParams
        ? cardBodyParams.findIndex(
            (param) => param.selected === parameterToInsert,
          ) + 1
        : 1;

      const insertionText = `{{${
        existingParamIndex || cardBodyParams.length + 1
      }}}`;
      updatedValue = `${textBefore}${insertionText}${textAfter}`;
      textarea.value = updatedValue;
      updateBodyCardParameters(textarea.value, cardIndex, componentIndex);
      newCursorPosition = textBefore.length + insertionText.length;
    }

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(newCursorPosition, newCursorPosition);
    }, 0);

    if (
      (!isIncard && !selectedNode.data.bodyParameters) ||
      (selectedNode.data.bodyParameters &&
        !selectedNode.data.bodyParameters.some(
          (param) => param.selected === parameterToInsert,
        ) &&
        !isIncard)
    ) {
      updateBodyParameters(updatedValue, parameterToInsert, selectedNode);
    } else if (isIncard) {
      handleUpdateCardComponent(
        selectedNode,
        cardIndex,
        componentIndex,
        { text: textarea.value },
        nodes,
        setNodes,
        setCards,
      );
    }
  };

  const addUrlVariable = async (ref, buttonNumber) => {
    const textarea = ref.current;

    const updatedValue = `${textarea.value}{{url_variable}}`;
    textarea.value = updatedValue;

    const buttonConfigs = [
      {
        sourceHandle: "urlA",
        buttonKey: "urlButton",
        text: urlButton,
      },
      {
        sourceHandle: "urlB",
        buttonKey: "urlButton2",
        text: urlButton2,
      },
    ];

    const buttonConfig = buttonConfigs[buttonNumber - 1];

    if (isInPanel) {
      const edgeToDelete = edges.find(
        (e) =>
          e.source === selectedNode.id &&
          e.sourceHandle === buttonConfig.sourceHandle,
      );

      if (edgeToDelete) {
        deleteEdge(edgeToDelete, setEdges, api);
      }

      handleMessageChange(
        buttonConfig.buttonKey,
        {
          text: buttonConfig.text,
          url: textarea.value.replace(/\{\{[^}]+\}\}/g, "{{1}}"),
          trackingEnabled:
            selectedNode?.data?.[buttonConfig.buttonKey]?.trackingEnabled ??
            true,
        },
        nodes,
        setNodes,
      );
    }
    if (buttonConfig.buttonKey === "urlButton") {
      setUrlButtonLink(textarea.value);
    } else if (buttonConfig.buttonKey === "urlButton2") {
      setUrlButtonLink2(textarea.value);
    }
  };

  const handleAddUpsellLink = useCallback(() => {
    // Set the URL to the upsell format
    const upsellUrl = `https://${shop?.domain}/{{1}}`;

    if (isInPanel) {
      setModifications(true);
      handleMessageChange(
        "urlButton",
        {
          text: "Compléter ma commande",
          url: upsellUrl,
          trackingEnabled: true,
        },
        nodes,
        setNodes,
        selectedNode,
      );
    } else {
      setUrlButton("Compléter ma commande");
      setUrlButtonLink(upsellUrl);
    }

    // Open the ProductPicker dialog
    if (isInPanel && handleOpenProductPicker) {
      handleOpenProductPicker();
    }
  }, [
    shop,
    isInPanel,
    nodes,
    setNodes,
    selectedNode,
    setModifications,
    handleOpenProductPicker,
  ]);

  useEffect(() => {
    if (
      urlButtonLink ===
        "https://camion-et-tracteurs.myshopify.com/71969669393/checkouts/{{1}}" &&
      shop
    ) {
      setUrlButtonLink(`https://${shop?.domain}/${shop?.id}/checkouts/{{1}}`);
      if (isInPanel) {
        handleMessageChange(
          "urlButton",
          {
            text: selectedNode?.data?.urlButton?.text,
            url: `https://${shop?.domain}/${shop?.id}/checkouts/{{1}}`,
          },
          nodes,
          setNodes,
          selectedNode,
        );
      }
    } else if (
      urlButtonLink ===
        "https://camion-et-tracteurs.myshopify.com/products/{{1}}" &&
      shop
    ) {
      setUrlButtonLink(`https://${shop?.domain}/products/{{1}}`);
      if (isInPanel) {
        handleMessageChange(
          "urlButton",
          {
            text: selectedNode?.data?.urlButton?.text,
            url: `https://${shop?.domain}/products/{{1}}`,
          },
          nodes,
          setNodes,
          selectedNode,
        );
      }
    }
  }, [urlButtonLink, shop]);

  return (
    <>
      <div className={nameClasses}>
        <div className="flex-1">
          <Field>
            <Legend>Template name</Legend>
            <Input
              name="template_name"
              ref={templateNameRef}
              value={templateName}
              onChange={(e) => {
                setTemplateName(e.target.value);
              }}
              onBlur={(e) => {
                setTemplateNameError(
                  formChecks.checkTemplateName(e.target.value),
                );
              }}
              autoComplete="off"
              className="border-transparent focus:border-transparent focus:ring-0 focus:outline-gray"
              invalid={!!templateNameError}
            />
            {templateNameError && (
              <Field>
                <ErrorMessage>{templateNameError}</ErrorMessage>
              </Field>
            )}
            <Description>
              {cleanTemplateName(
                templateName ?? selectedNode?.data?.whatsappTemplateName,
              ) === templateName
                ? ""
                : cleanTemplateName(
                    templateName ?? selectedNode?.data?.whatsappTemplateName,
                  )}
            </Description>
          </Field>
        </div>

        {isInPanel ? (
          <Field className="mb-[20px]">
            <Legend>Template category</Legend>
            <SelectInput
              placeholder="Utility"
              value={selectedCategory}
              onChange={(value) => {
                setSelectedCategory(value);
                handleMessageChange(
                  "category",
                  value,
                  nodes,
                  setNodes,
                  selectedNode,
                );
                setModifications(true);
                const _isLimitedTimeOffer =
                  value === WhatsappTemplateCategoryEnum.LIMITED_TIME_OFFER;
                const _limitedTimeOffer = _isLimitedTimeOffer
                  ? selectedNode.data.limitedTimeOffer || {
                      text: "",
                      expirationType: LimitedTimeOfferExpirationTypeEnum.DELAY,
                    }
                  : null;
                const _copyCodeButton = _isLimitedTimeOffer
                  ? selectedNode.data.copyCodeButton || {
                      text: "",
                    }
                  : null;
                const _isCarousel =
                  value === WhatsappTemplateCategoryEnum.CAROUSEL;
                const _cards = _isCarousel
                  ? [
                      {
                        components: [
                          {
                            link: "",
                            type: "HEADER",
                            format: "IMAGE",
                          },
                          {
                            text: "",
                            type: "BODY",
                            bodyParameters: [],
                          },
                          {
                            type: "BUTTONS",
                            buttons: [
                              {
                                text: "",
                                type: "QUICK_REPLY",
                              },
                            ],
                          },
                        ],
                      },
                      {
                        components: [
                          {
                            link: "",
                            type: "HEADER",
                            format: "IMAGE",
                          },
                          {
                            text: "",
                            type: "BODY",
                            bodyParameters: [],
                          },
                          {
                            type: "BUTTONS",
                            buttons: [
                              {
                                text: "",
                                type: "QUICK_REPLY",
                              },
                            ],
                          },
                        ],
                      },
                    ]
                  : null;
                if (_isLimitedTimeOffer) {
                  setFooter("");
                  handleMessageChange(
                    "footer",
                    "",
                    nodes,
                    setNodes,
                    selectedNode,
                  );
                  setUrlButton2(null);
                  setUrlButtonLink2(null);
                  if (numberOfUrl !== 1) {
                    setNumberOfUrl(1);
                  }
                } else if (_isCarousel) {
                  setFooter("");
                  handleMessageChange(
                    "footer",
                    "",
                    nodes,
                    setNodes,
                    selectedNode,
                  );
                  setSelectedHeader(["none"]);
                  setNumberOfUrl(0);
                  setNumberOfQuickReplies(0);
                  setQuickReply1(null);
                  setQuickReply2(null);
                  setQuickReply3(null);
                  setUrlButton("");
                  setUrlButtonLink("");
                  setUrlButton2(null);
                  setUrlButtonLink2(null);
                  handleMessageChange(
                    "buttons",
                    [],
                    nodes,
                    setNodes,
                    selectedNode,
                  );
                  handleMessageChange(
                    "headerParameters",
                    {},
                    nodes,
                    setNodes,
                    selectedNode,
                  );
                }
                setIsLimitedTimeOffer(_isLimitedTimeOffer);
                setLimitedTimeOffer(_limitedTimeOffer);
                setCards(_cards);
                handleMessageChange(
                  "limitedTimeOffer",
                  _limitedTimeOffer,
                  nodes,
                  setNodes,
                  selectedNode,
                );
                handleMessageChange(
                  "copyCodeButton",
                  _copyCodeButton,
                  nodes,
                  setNodes,
                  selectedNode,
                );
                handleMessageChange(
                  "cards",
                  _cards,
                  nodes,
                  setNodes,
                  selectedNode,
                );
                handleMessageChange(
                  "urlButton",
                  null,
                  nodes,
                  setNodes,
                  selectedNode,
                );
                handleMessageChange(
                  "urlButton2",
                  null,
                  nodes,
                  setNodes,
                  selectedNode,
                );
              }}
              options={categoriesOptions}
              selected={selectedCategory}
            />
          </Field>
        ) : (
          <div className="w-96">
            <div className="ml-[5em] mb-[20px]">
              <Fieldset>
                <Legend>Template category</Legend>
                <RadioGroup
                  name="category"
                  defaultValue={selectedCategory}
                  onChange={handleCategoryChange}
                >
                  <RadioField>
                    <Radio value={WhatsappTemplateCategoryEnum.MARKETING} />
                    <Label>Marketing</Label>
                    <Description>
                      For marketing campaigns, promotions, offers etc...
                    </Description>
                  </RadioField>
                  <RadioField>
                    <Radio value={WhatsappTemplateCategoryEnum.UTILITY} />
                    <Label>Utility</Label>
                    <Description>
                      For messages related to an order placed, without any
                      incentive to buy, such as order tracking etc...
                    </Description>
                  </RadioField>
                  <RadioField>
                    <Radio
                      value={WhatsappTemplateCategoryEnum.LIMITED_TIME_OFFER}
                    />
                    <Label>Limited-time offer</Label>
                    <Description>
                      For messages related to a limited-time offer...
                    </Description>
                  </RadioField>
                </RadioGroup>
              </Fieldset>
            </div>
          </div>
        )}
      </div>

      <Divider />

      <div className="mx-auto flex w-full max-w-7xl items-start pb-5 gap-x-8">
        <main className="flex-1">
          {!cards && (
            <>
              <Fieldset>
                <Legend>Header</Legend>
                <RadioGroup
                  name="header"
                  value={selectedHeader[0]}
                  onChange={handleHeaderChange}
                >
                  <RadioField>
                    <Radio value="none" />
                    <Label>None</Label>
                    <Description>There will be no header</Description>
                  </RadioField>
                  <RadioField>
                    <Radio value="image" />
                    <Label>Image</Label>
                    <Description>Add a.png or .jpeg</Description>
                  </RadioField>
                  <RadioField>
                    <Radio value="video" />
                    <Label>Video</Label>
                    <Description>Add a .mp4</Description>
                  </RadioField>
                </RadioGroup>
              </Fieldset>
              {selectedHeader[0] === "image" && isInPanel && (
                <>
                  <Field>
                    <Legend>Image url</Legend>
                    <Select
                      className="mb-2"
                      placeholder="Select a parameter"
                      label={`Header image`}
                      options={imageParamOptions}
                      onChange={(e) => {
                        let newParameters = {
                          type: "image",
                          image:
                            e.target.value === "custom"
                              ? { link: selectedNode.data.image }
                              : e.target.value,
                        };
                        if (isInPanel) {
                          handleMessageChange(
                            "image",
                            selectedNode.data.image,
                            nodes,
                            setNodes,
                            selectedNode,
                          );
                          handleMessageChange(
                            "headerParameters",
                            newParameters,
                            nodes,
                            setNodes,
                            selectedNode,
                          );
                        }
                      }}
                      value={
                        selectedNode?.data.headerParameters?.image ===
                        "shopifyProductImage"
                          ? "shopifyProductImage"
                          : "custom"
                      }
                    >
                      {imageParamOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  {selectedNode.data.headerParameters?.image !==
                    "shopifyProductImage" && (
                    <>
                      <Field>
                        <Input
                          name="Image URL"
                          key="Image_URL"
                          value={headerImageLink}
                          onChange={(e) => {
                            setHeaderImageLink(e.target.value);
                            if (isInPanel) {
                              setModifications(true);
                              handleMessageChange(
                                "image",
                                e.target.value,
                                nodes,
                                setNodes,
                                selectedNode,
                              );
                              handleMessageChange(
                                "headerParameters",
                                {
                                  type: "image",
                                  image: {
                                    link: e.target.value,
                                  },
                                },
                                nodes,
                                setNodes,
                                selectedNode,
                              );
                            }
                          }}
                          placeholder="https://exemple.com/"
                          autoComplete="off"
                          maxLength={2000}
                        />
                      </Field>
                      <br />
                      <Dragger
                        name="file"
                        showUploadList={false}
                        maxCount={1}
                        accept="image/jpeg,image/png,application/pdf"
                        disabled={isUploading}
                        multiple={false}
                        height={250}
                        beforeUpload={(file) => {
                          const isValid = handleBeforeUploadFile({
                            file: file,
                            setFile: setFile,
                            selectedHeader: "image",
                            setIsPanelOpened: setIsPanelOpened,
                            setIsConvertCompressDialogOpen:
                              setIsConvertCompressDialogOpen,
                            selectedNode: selectedNode,
                            selectedNodeConvertCompress:
                              selectedNodeConvertCompress,
                            setSelectedNodeConvertCompress:
                              setSelectedNodeConvertCompress,
                          });
                          return isValid;
                        }}
                        action={(file) =>
                          handleUploadFile({
                            file: file,
                            setIsUploading: setIsUploading,
                            user: user,
                            selectedNode: selectedNode,
                            nodes: nodes,
                            setHeaderImageLink: setHeaderImageLink,
                            setNodes: setNodes,
                            setCards: setCards,
                            setModifications: setModifications,
                            carouselCardIndex: null,
                            carouselComponentIndex: null,
                            selectedHeader: "image",
                          })
                        }
                        onDrop={(e) => {
                          console.log("Dropped files", e.dataTransfer.files);
                        }}
                      >
                        <p className="ant-upload-drag-icon">
                          <InboxOutlined />
                        </p>
                        <p className="ant-upload-text">
                          Click or drag-and-drop a file to import it
                        </p>
                        <p className="ant-upload-hint">
                          Please upload a .jpeg or .png file. It must be less
                          than 5MB.
                        </p>
                      </Dragger>
                    </>
                  )}
                </>
              )}

              {selectedHeader[0] === "video" && isInPanel && (
                <>
                  <Field>
                    <Legend>Video URL</Legend>
                    <Input
                      name="Video URL"
                      key="video_url"
                      value={headerVideoLink}
                      onChange={(e) => {
                        setHeaderVideoLink(e.target.value);
                        if (isInPanel) {
                          setModifications(true);
                          handleMessageChange(
                            "video",
                            e.target.value,
                            nodes,
                            setNodes,
                            selectedNode,
                          );
                          handleMessageChange(
                            "headerParameters",
                            {
                              type: "video",
                              video: {
                                link: e.target.value,
                              },
                            },
                            nodes,
                            setNodes,
                          );
                        }
                      }}
                      placeholder="https://exemple.com/"
                      autoComplete="off"
                      maxLength={2000}
                    />
                  </Field>
                  <br />

                  <Dragger
                    name="file"
                    showUploadList={false}
                    maxCount={1}
                    accept="video/mp4"
                    disabled={isUploading}
                    multiple={false}
                    height={250}
                    beforeUpload={(file) => {
                      const isValid = handleBeforeUploadFile({
                        file: file,
                        selectedHeader: "video",
                      });
                      return isValid;
                    }}
                    action={async (file) => {
                      await handleUploadFile({
                        file: file,
                        setIsUploading: setIsUploading,
                        user: user,
                        selectedNode: selectedNode,
                        nodes: nodes,
                        setHeaderVideoLink: setHeaderVideoLink,
                        setNodes: setNodes,
                        setCards: setCards,
                        setModifications: setModifications,
                        selectedHeader: "video",
                        isInPanel: true,
                      });
                    }}
                    onDrop={() => {}}
                  >
                    <p className="ant-upload-drag-icon">
                      <InboxOutlined />
                    </p>
                    <p className="ant-upload-text">
                      Click or drag-and-drop a file to import it
                    </p>
                    <p className="ant-upload-hint">
                      Please upload a .mp4 file smaller than 16MB
                    </p>
                  </Dragger>
                </>
              )}
            </>
          )}

          {isLimitedTimeOffer && limitedTimeOffer && (
            <>
              <LimitedTimeOffer
                data={limitedTimeOffer}
                isInPanel={isInPanel}
                handleLimitedTimeOfferChange={handleLimitedTimeOfferChange}
                isEditing={true}
                setModifications={setModifications}
                onBlur={() =>
                  setLimitedTimeOfferTextError(
                    formChecks.checkLimitedTimeOfferText(limitedTimeOffer),
                  )
                }
                invalid={!!limitedTimeOfferTextError}
                error={limitedTimeOfferTextError}
              />
            </>
          )}

          <Fieldset>
            <Field>
              {!cards && <Divider className="mt-5" />}

              <div className="flex justify-between items-end">
                <Legend>Body</Legend>
                <Tooltip
                  size="4"
                  content={
                    <div>
                      Text formatting:
                      <br />
                      *Asterisks make the text bold*
                      <br />
                      _Underscores make the text italic_
                    </div>
                  }
                />
              </div>
              <Textarea
                name="body"
                ref={bodyRef}
                value={body}
                onChange={(e) => {
                  handleBodyChange(e);
                  if (isInPanel) {
                    setModifications(true);
                  }
                }}
                onBlur={(e) =>
                  setBodyErrorMsg(formChecks.checkBody(e.target.value))
                }
                invalid={!!bodyErrorMsg}
                maxLength={1024}
                showCharacterCount
              />
              {bodyErrorMsg && (
                <Field>
                  <ErrorMessage>{bodyErrorMsg}</ErrorMessage>
                </Field>
              )}
              {isInPanel && (
                <div className="flex mt-2 w-full justify-between">
                  <div className="flex-grow mr-1">
                    <SelectInput
                      placeholder="Select a parameter"
                      value={parameterToInsert}
                      onChange={(value) => {
                        setParameterToInsert(value);
                      }}
                      options={paramOptions}
                      selected={parameterToInsert}
                    />
                  </div>

                  <Button
                    color="secondaryButton"
                    disabled={parameterToInsert === ""}
                    onClick={() => {
                      addVariable(false);
                    }}
                  >
                    Add parameter
                  </Button>
                </div>
              )}

              <Description>
                {(body?.includes("{{2}}") || body?.includes("{{3}}")) &&
                !body?.includes("{{1}}")
                  ? "⚠️ Your parameters must be numbered starting with {{1}}, then {{2}}... "
                  : "or write {{1}}, then {{2}}..."}
              </Description>
            </Field>

            {isInPanel
              ? newBodyParameters.map((p, index) => (
                  <Field key={index + "field"}>
                    <Divider className="mt-5 mb-3" />
                    <Label key={index + "label"}>{`Parameter ${
                      index + 1
                    }`}</Label>
                    <Select
                      className="mb-2"
                      key={index + "bodyParam"}
                      placeholder="Select a parameter"
                      onChange={(e) => {
                        const selectedValue = e.target.value;
                        const updatedParams = [...newBodyParameters];
                        updatedParams[index] = {
                          type: "text",
                          selected: selectedValue,
                        };
                        setNewBodyParameters(updatedParams);
                        setModifications(true);
                        handleMessageChange(
                          "bodyParameters",
                          updatedParams,
                          nodes,
                          setNodes,
                          selectedNode,
                        );
                      }}
                      value={newBodyParameters[index].selected}
                    >
                      {paramOptions.map((option, i) => (
                        <option
                          key={`${option.value}-${i}`}
                          value={option.value}
                        >
                          {option.label}
                        </option>
                      ))}
                    </Select>
                    <div className="flex justify-between items-center">
                      <Label key={index + "defaultLabel"}>
                        {"Default value"}
                      </Label>
                      <Input
                        className="max-w-[75%]"
                        type="text"
                        key={index + "defaultInput"}
                        placeholder="Default value if parameter is empty"
                        autoComplete="off"
                        value={
                          selectedNode.data.bodyParameters[index]?.default || ""
                        }
                        onChange={(e) => {
                          var newParams = [
                            ...(selectedNode.data?.bodyParameters ?? []),
                          ];
                          newParams[index] = {
                            ...newParams[index],
                            default: e.target.value,
                          };
                          handleMessageChange(
                            "bodyParameters",
                            newParams,
                            nodes,
                            setNodes,
                          );
                        }}
                      />
                    </div>
                  </Field>
                ))
              : null}

            {!isLimitedTimeOffer && !cards && (
              <Field>
                <Divider className="mt-5 mb-3" />

                <Legend>Footer</Legend>
                <InputGroup>
                  <Input
                    name="footer"
                    value={footer}
                    onChange={handleFooterChange}
                    maxLength={60}
                    showCharacterCount
                    autoComplete="off"
                  />
                </InputGroup>
                <Divider className="mt-5" />
              </Field>
            )}
          </Fieldset>

          {!cards && (
            <div
              className={clsx("buttons", {
                "limited-time-offer": isLimitedTimeOffer,
              })}
            >
              {isLimitedTimeOffer && (
                <Fieldset className="copy-code-button">
                  <Field>
                    <Legend>Copy code</Legend>
                    <div className="flex flex-row">
                      <InputGroup className="flex relative items-center">
                        <ClipboardDocumentIcon />
                        <Input
                          className="focus:ring-0"
                          placeholder="Copy code text"
                          value={copyCodeButton?.text ?? ""}
                          onChange={handleCopyCodeButtonChange}
                          maxLength={15}
                          showCharacterCount
                          autoComplete="off"
                        />
                      </InputGroup>
                    </div>
                  </Field>
                </Fieldset>
              )}

              <Fieldset className="quick-replies-buttons">
                <Field>
                  <Legend>Buttons</Legend>
                  {numberOfQuickReplies >= 1 && (
                    <>
                      <div className="flex flex-row">
                        <InputGroup className="flex relative items-center">
                          <ArrowUturnLeftIcon />
                          <Input
                            className="focus:ring-0"
                            placeholder="Button title"
                            value={quickReply1}
                            ref={quickReply1Ref}
                            onChange={(e) => {
                              setQuickReply1(e.target.value);
                              if (isInPanel) {
                                setModifications(true);
                                updateReplyButton(0, e.target.value);
                              }
                            }}
                            onBlur={(e) =>
                              setQuickReply1ErrorMsg(
                                formChecks.checkQuickReply(e.target.value),
                              )
                            }
                            invalid={!!quickReply1ErrorMsg}
                            maxLength={25}
                            showCharacterCount
                            autoComplete="off"
                          />
                        </InputGroup>
                        {numberOfQuickReplies === 1 && (
                          <Button
                            color="optionButton"
                            className="ml-2"
                            onClick={() => {
                              setQuickReply1("");
                              setQuickReply1ErrorMsg("");
                              setCustomField1ErrorMsg("");
                              setNumberOfQuickReplies((n) => n - 1);
                              if (isInPanel) {
                                setModifications(true);
                                removeReplyButton(0);
                              }
                            }}
                          >
                            <TrashIcon />
                          </Button>
                        )}
                      </div>
                      {quickReply1ErrorMsg && (
                        <Field>
                          <ErrorMessage>{quickReply1ErrorMsg}</ErrorMessage>
                        </Field>
                      )}
                      {customFieldOptions.length > 0 && (
                        <div className="mt-3">
                          <div className="flex items-center gap-2 mb-2">
                            <Switch
                              color="yellow"
                              id="save-custom-field-0"
                              checked={
                                replyButtons?.[0]?.saveAsCustomField || false
                              }
                              onChange={(checked) => {
                                const updatedButtons = replyButtons
                                  ? [...replyButtons]
                                  : [];
                                updatedButtons[0] = {
                                  ...updatedButtons[0],
                                  title:
                                    updatedButtons[0]?.title || quickReply1,
                                  saveAsCustomField: checked,
                                };
                                setReplyButtons(updatedButtons);
                                if (isInPanel) {
                                  handleMessageChange(
                                    "buttons",
                                    updatedButtons,
                                    nodes,
                                    setNodes,
                                    selectedNode,
                                  );
                                }
                              }}
                              className="rounded border-yellow-300  bg-yellow-500 "
                            />
                            <label
                              htmlFor="save-custom-field-0"
                              className="text-sm text-gray-700 cursor-pointer"
                            >
                              Save clicks as custom field
                            </label>
                          </div>
                          {replyButtons?.[0]?.saveAsCustomField && (
                            <div>
                              <SelectInput
                                placeholder="Select a custom field"
                                value={replyButtons?.[0]?.customField || ""}
                                onChange={(value) => {
                                  const updatedButtons = replyButtons
                                    ? [...replyButtons]
                                    : [];
                                  updatedButtons[0] = {
                                    ...updatedButtons[0],
                                    title:
                                      updatedButtons[0]?.title || quickReply1,
                                    customField: value,
                                  };
                                  setReplyButtons(updatedButtons);
                                  if (isInPanel) {
                                    handleMessageChange(
                                      "buttons",
                                      updatedButtons,
                                      nodes,
                                      setNodes,
                                      selectedNode,
                                    );
                                  }
                                }}
                                options={customFieldOptions}
                                selected={replyButtons?.[0]?.customField || ""}
                                invalid={
                                  !!customField1ErrorMsg &&
                                  !replyButtons?.[0]?.customField
                                }
                              />
                              {!!customField1ErrorMsg &&
                                !replyButtons?.[0]?.customField && (
                                  <ErrorMessage>
                                    {customField1ErrorMsg}
                                  </ErrorMessage>
                                )}
                              <Input
                                placeholder="Custom field value"
                                className="mt-2"
                                value={
                                  replyButtons?.[0]?.customFieldValue || ""
                                }
                                onChange={(e) => {
                                  const updatedButtons = replyButtons
                                    ? [...replyButtons]
                                    : [];
                                  updatedButtons[0] = {
                                    ...updatedButtons[0],
                                    customFieldValue: e.target.value,
                                  };
                                  setReplyButtons(updatedButtons);
                                  if (isInPanel) {
                                    handleMessageChange(
                                      "buttons",
                                      updatedButtons,
                                      nodes,
                                      setNodes,
                                      selectedNode,
                                    );
                                  }
                                }}
                                onBlur={() =>
                                  setCustomField1ErrorMsg(
                                    formChecks.checkCustomField(
                                      replyButtons?.[0]?.saveAsCustomField,
                                      replyButtons?.[0]?.customField,
                                    ),
                                  )
                                }
                              />
                            </div>
                          )}
                        </div>
                      )}
                      <br />
                    </>
                  )}

                  {numberOfQuickReplies >= 2 && (
                    <>
                      <div className="flex flex-row">
                        <InputGroup>
                          <ArrowUturnLeftIcon />
                          <Input
                            value={quickReply2}
                            ref={quickReply2Ref}
                            placeholder="Button title"
                            onChange={(e) => {
                              setQuickReply2(e.target.value);
                              if (isInPanel) {
                                setModifications(true);
                                updateReplyButton(1, e.target.value);
                              }
                            }}
                            onBlur={(e) =>
                              setQuickReply2ErrorMsg(
                                formChecks.checkQuickReply(e.target.value),
                              )
                            }
                            invalid={!!quickReply2ErrorMsg}
                            maxLength={25}
                            showCharacterCount
                            autoComplete="off"
                          />
                        </InputGroup>
                        {numberOfQuickReplies === 2 && (
                          <Button
                            color="optionButton"
                            className="ml-2"
                            onClick={() => {
                              setQuickReply2("");
                              setQuickReply2ErrorMsg("");
                              setCustomField2ErrorMsg("");
                              setNumberOfQuickReplies((n) => n - 1);
                              if (isInPanel) {
                                setModifications(true);
                                removeReplyButton(1);
                              }
                            }}
                          >
                            <TrashIcon />
                          </Button>
                        )}
                      </div>
                      {quickReply2ErrorMsg && (
                        <Field>
                          <ErrorMessage>{quickReply2ErrorMsg}</ErrorMessage>
                        </Field>
                      )}
                      {customFieldOptions.length > 0 && (
                        <div className="mt-3">
                          <div className="flex items-center gap-2 mb-2">
                            <Switch
                              color="yellow"
                              id="save-custom-field-1"
                              checked={
                                replyButtons?.[1]?.saveAsCustomField || false
                              }
                              onChange={(checked) => {
                                const updatedButtons = replyButtons
                                  ? [...replyButtons]
                                  : [];
                                updatedButtons[1] = {
                                  ...updatedButtons[1],
                                  title:
                                    updatedButtons[1]?.title || quickReply2,
                                  saveAsCustomField: checked,
                                };
                                setReplyButtons(updatedButtons);
                                if (isInPanel) {
                                  handleMessageChange(
                                    "buttons",
                                    updatedButtons,
                                    nodes,
                                    setNodes,
                                    selectedNode,
                                  );
                                }
                              }}
                              className="rounded border-yellow-300  bg-yellow-500 "
                            />
                            <label
                              htmlFor="save-custom-field-1"
                              className="text-sm text-gray-700 cursor-pointer"
                            >
                              Save clicks as custom field
                            </label>
                          </div>
                          {replyButtons?.[1]?.saveAsCustomField && (
                            <div>
                              <SelectInput
                                placeholder="Select a custom field"
                                value={replyButtons?.[1]?.customField || ""}
                                onChange={(value) => {
                                  const updatedButtons = replyButtons
                                    ? [...replyButtons]
                                    : [];
                                  updatedButtons[1] = {
                                    ...updatedButtons[1],
                                    title:
                                      updatedButtons[1]?.title || quickReply2,
                                    customField: value,
                                  };
                                  setReplyButtons(updatedButtons);
                                  if (isInPanel) {
                                    handleMessageChange(
                                      "buttons",
                                      updatedButtons,
                                      nodes,
                                      setNodes,
                                      selectedNode,
                                    );
                                  }
                                }}
                                options={customFieldOptions}
                                disabled={!replyButtons?.[1]?.saveAsCustomField}
                                selected={replyButtons?.[1]?.customField || ""}
                                invalid={
                                  !!customField2ErrorMsg &&
                                  !replyButtons?.[1]?.customField
                                }
                              />
                              {customField2ErrorMsg &&
                                !replyButtons?.[1]?.customField && (
                                  <ErrorMessage>
                                    {customField2ErrorMsg}
                                  </ErrorMessage>
                                )}
                              <Input
                                className="mt-2"
                                value={
                                  replyButtons?.[1]?.customFieldValue || ""
                                }
                                onBlur={() =>
                                  setCustomField2ErrorMsg(
                                    formChecks.checkCustomField(
                                      replyButtons?.[1]?.saveAsCustomField,
                                      replyButtons?.[1]?.customField,
                                    ),
                                  )
                                }
                                onChange={(value) => {
                                  const updatedButtons = replyButtons
                                    ? [...replyButtons]
                                    : [];
                                  updatedButtons[1] = {
                                    ...updatedButtons[1],
                                    title:
                                      updatedButtons[1]?.title || quickReply2,
                                    customFieldValue: value,
                                  };
                                  setReplyButtons(updatedButtons);
                                  if (isInPanel) {
                                    handleMessageChange(
                                      "buttons",
                                      updatedButtons,
                                      nodes,
                                      setNodes,
                                      selectedNode,
                                    );
                                  }
                                }}
                                placeholder="Custom field value"
                              />
                            </div>
                          )}
                        </div>
                      )}
                      <br />
                    </>
                  )}
                  {numberOfQuickReplies >= 3 && (
                    <>
                      <div className="flex flex-row">
                        <InputGroup>
                          <ArrowUturnLeftIcon />
                          <Input
                            value={quickReply3}
                            ref={quickReply3Ref}
                            placeholder="Button title"
                            onChange={(e) => {
                              setQuickReply3(e.target.value);
                              if (isInPanel) {
                                setModifications(true);
                                updateReplyButton(2, e.target.value);
                              }
                            }}
                            onBlur={(e) =>
                              setQuickReply3ErrorMsg(
                                formChecks.checkQuickReply(e.target.value),
                              )
                            }
                            invalid={!!quickReply3ErrorMsg}
                            maxLength={25}
                            showCharacterCount
                            autoComplete="off"
                          />
                        </InputGroup>
                        {numberOfQuickReplies === 3 && (
                          <Button
                            color="optionButton"
                            className="ml-2"
                            onClick={() => {
                              setQuickReply3("");
                              setCustomField3ErrorMsg("");
                              setNumberOfQuickReplies((n) => n - 1);
                              if (isInPanel) {
                                setModifications(true);
                                removeReplyButton(2);
                              }
                            }}
                          >
                            <TrashIcon />
                          </Button>
                        )}
                      </div>
                      {quickReply3ErrorMsg && (
                        <Field>
                          <ErrorMessage>{quickReply3ErrorMsg}</ErrorMessage>
                        </Field>
                      )}
                      {customFieldOptions.length > 0 && (
                        <div className="mt-3">
                          <div className="flex items-center gap-2 mb-2">
                            <Switch
                              color="yellow"
                              id="save-custom-field-2"
                              checked={
                                replyButtons?.[2]?.saveAsCustomField || false
                              }
                              onChange={(checked) => {
                                const updatedButtons = replyButtons
                                  ? [...replyButtons]
                                  : [];
                                updatedButtons[2] = {
                                  ...updatedButtons[2],
                                  title:
                                    updatedButtons[2]?.title || quickReply3,
                                  saveAsCustomField: checked,
                                };
                                setReplyButtons(updatedButtons);
                                if (isInPanel) {
                                  handleMessageChange(
                                    "buttons",
                                    updatedButtons,
                                    nodes,
                                    setNodes,
                                    selectedNode,
                                  );
                                }
                              }}
                              className="rounded border-gray-300  bg-yellow-500 "
                            />
                            <label
                              htmlFor="save-custom-field-2"
                              className="text-sm text-gray-700 cursor-pointer"
                            >
                              Save clicks as custom field
                            </label>
                          </div>
                          {replyButtons?.[2]?.saveAsCustomField && (
                            <div>
                              <SelectInput
                                placeholder="Select a custom field"
                                value={replyButtons?.[2]?.customField || ""}
                                onChange={(value) => {
                                  const updatedButtons = replyButtons
                                    ? [...replyButtons]
                                    : [];
                                  updatedButtons[2] = {
                                    ...updatedButtons[2],
                                    title:
                                      updatedButtons[2]?.title || quickReply3,
                                    customField: value,
                                  };
                                  setReplyButtons(updatedButtons);
                                  if (isInPanel) {
                                    handleMessageChange(
                                      "buttons",
                                      updatedButtons,
                                      nodes,
                                      setNodes,
                                      selectedNode,
                                    );
                                  }
                                }}
                                options={customFieldOptions}
                                disabled={!replyButtons?.[2]?.saveAsCustomField}
                                selected={replyButtons?.[2]?.customField || ""}
                                invalid={
                                  !!customField3ErrorMsg &&
                                  !replyButtons?.[2]?.customField
                                }
                              />
                              {customField3ErrorMsg &&
                                !replyButtons?.[2]?.customField && (
                                  <ErrorMessage>
                                    {customField3ErrorMsg}
                                  </ErrorMessage>
                                )}
                              <Input
                                placeholder="Custom field value"
                                className="mt-2"
                                value={
                                  replyButtons?.[2]?.customFieldValue || ""
                                }
                                onBlur={() => {
                                  console.log(replyButtons);
                                  setCustomField3ErrorMsg(
                                    formChecks.checkCustomField(
                                      replyButtons?.[2]?.saveAsCustomField,
                                      replyButtons?.[2]?.customField,
                                    ),
                                  );
                                }}
                                onChange={(value) => {
                                  const updatedButtons = replyButtons
                                    ? [...replyButtons]
                                    : [];
                                  updatedButtons[2] = {
                                    ...updatedButtons[2],
                                    title:
                                      updatedButtons[2]?.title || quickReply2,
                                    customFieldValue: value,
                                  };
                                  setReplyButtons(updatedButtons);
                                  if (isInPanel) {
                                    handleMessageChange(
                                      "buttons",
                                      updatedButtons,
                                      nodes,
                                      setNodes,
                                      selectedNode,
                                    );
                                  }
                                }}
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                  {numberOfQuickReplies < 3 && (
                    <>
                      <Button
                        color="secondaryButton"
                        onClick={() => setNumberOfQuickReplies((n) => n + 1)}
                      >
                        Add button
                      </Button>
                    </>
                  )}
                </Field>
              </Fieldset>

              <Fieldset className="url-button">
                <Field>
                  <Legend>Links</Legend>
                  {numberOfUrl >= 1 && (
                    <>
                      {/* Tracking Toggle for URL Button 1 - only available before template submission */}
                      {!selectedNode?.data?.isPending && (
                        <Field>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Legend>Enable click tracking</Legend>
                              <Tooltip
                                content={
                                  urlButtonLink?.includes("{{")
                                    ? "Tracking is automatically disabled for URLs with variables"
                                    : "Track clicks and engagement metrics for this link"
                                }
                                size="4"
                                color="gray-400"
                                iconClassName="flex items-center justify-center -mt-0.5"
                              />
                            </div>
                            <SwitchOnOff
                              checked={
                                urlButtonLink?.includes("{{")
                                  ? false
                                  : (selectedNode?.data?.urlButton
                                      ?.trackingEnabled ?? true)
                              }
                              onChange={(enabled) => {
                                if (
                                  isInPanel &&
                                  !urlButtonLink?.includes("{{")
                                ) {
                                  setModifications(true);
                                  handleMessageChange(
                                    "urlButton",
                                    {
                                      text: urlButton,
                                      url: urlButtonLink,
                                      trackingEnabled: enabled,
                                    },
                                    nodes,
                                    setNodes,
                                    selectedNode,
                                  );
                                }
                              }}
                              color="dark/zinc"
                              disabled={urlButtonLink?.includes("{{")}
                            />
                          </div>
                        </Field>
                      )}

                      <div className="flex flex-row mb-2">
                        <InputGroup>
                          <ArrowTopRightOnSquareIcon />
                          <Input
                            value={urlButton}
                            placeholder="Link title"
                            onChange={(e) => {
                              setUrlButton(e.target.value);
                              if (isInPanel) {
                                setModifications(true);
                                handleMessageChange(
                                  "urlButton",
                                  {
                                    text: e.target.value,
                                    url: urlButtonLink,
                                    trackingEnabled:
                                      selectedNode?.data?.urlButton
                                        ?.trackingEnabled ?? true,
                                  },
                                  nodes,
                                  setNodes,
                                  selectedNode,
                                );
                              }
                            }}
                            onBlur={(e) =>
                              setUrlButtonErrorMsg(
                                formChecks.checkUrlButton(
                                  e.target.value,
                                  urlButtonLink,
                                ),
                              )
                            }
                            invalid={!!urlButtonErrorMsg}
                            maxLength={25}
                            showCharacterCount
                            autoComplete="off"
                          />
                        </InputGroup>

                        {/* The URL button is mandatory for LTO WA Template*/}
                        {!isLimitedTimeOffer && numberOfUrl === 1 && (
                          <Button
                            color="white"
                            className="border ml-2"
                            onClick={() => {
                              setUrlButton("");
                              setUrlButtonLink("");
                              setUrlButtonErrorMsg("");
                              setUrlButtonLinkErrorMsg("");
                              setNumberOfUrl((n) => n - 1);
                              if (isInPanel) {
                                setModifications(true);
                                handleMessageChange(
                                  "urlButton",
                                  null,
                                  nodes,
                                  setNodes,
                                  selectedNode,
                                );
                                removeEdgeOnUrlButton(0);
                              }
                            }}
                          >
                            <TrashIcon />
                          </Button>
                        )}
                      </div>
                      <Input
                        value={urlButtonLink.replace(
                          /\{\{[^}]+\}\}/g,
                          "{{url_variable}}",
                        )}
                        ref={urlButtonLinkRef}
                        disabled={campaign?.automationType === "abandoned-cart"}
                        placeholder="Link URL"
                        onChange={(e) => {
                          setUrlButtonLink(
                            e.target.value.replace(
                              /\{\{[^}]+\}\}/g,
                              "{{url_variable}}",
                            ),
                          );
                          if (isInPanel) {
                            setModifications(true);
                            handleMessageChange(
                              "urlButton",
                              {
                                text: urlButton,
                                url: e.target.value,
                                trackingEnabled:
                                  selectedNode?.data?.urlButton
                                    ?.trackingEnabled ?? true,
                              },
                              nodes,
                              setNodes,
                              selectedNode,
                            );
                          }
                        }}
                        onBlur={(e) =>
                          setUrlButtonLinkErrorMsg(
                            formChecks.checkUrlButtonLink(
                              isLimitedTimeOffer,
                              e.target.value,
                              urlButton,
                            ),
                          )
                        }
                        invalid={!!urlButtonLinkErrorMsg}
                        autoComplete="off"
                      />
                      {urlButtonErrorMsg && (
                        <Field>
                          <ErrorMessage>{urlButtonErrorMsg}</ErrorMessage>
                        </Field>
                      )}
                      {urlButtonLinkErrorMsg && (
                        <Field>
                          <ErrorMessage>{urlButtonLinkErrorMsg}</ErrorMessage>
                        </Field>
                      )}
                      {urlVariableNotAtEnd1 && (
                        <ErrorMessage>
                          {"{{url_variable}}"} must be at the end of the URL
                        </ErrorMessage>
                      )}
                      <div className="flex gap-3 my-2">
                        {!(
                          campaign?.automationType === "order-confirmation" &&
                          integrations?.shopify === true &&
                          shopifyUpsellLineItems?.length > 0
                        ) && (
                          <Button
                            color="secondaryButton"
                            onClick={() => {
                              addUrlVariable(urlButtonLinkRef, 1);
                            }}
                            disabled={
                              urlButtonLink?.includes("{{url_variable}}") ||
                              urlButtonLink?.includes("{{1}}")
                            }
                          >
                            Add url variable
                          </Button>
                        )}

                        {campaign?.automationType === "order-confirmation" &&
                          integrations?.shopify === true && (
                            <Button
                              color="secondaryButton"
                              onClick={handleAddUpsellLink}
                            >
                              {shopifyUpsellLineItems?.length > 0 ? (
                                <ShoppingCartIcon className="h-4 w-4 mr-1" />
                              ) : (
                                <PlusIcon className="h-4 w-4 mr-1" />
                              )}
                              {shopifyUpsellLineItems?.length > 0
                                ? "Edit upsell products"
                                : "Insert upsell URL"}
                            </Button>
                          )}
                      </div>

                      {!isLimitedTimeOffer && numberOfUrl >= 2 && (
                        <>
                          {/* Tracking Toggle for URL Button 2 - only available before template submission */}
                          {!selectedNode?.data?.isPending && (
                            <Field>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <Legend>Enable click tracking</Legend>
                                  <Tooltip
                                    content={
                                      urlButtonLink2?.includes("{{")
                                        ? "Tracking is automatically disabled for URLs with variables"
                                        : "Track clicks and engagement metrics for this link"
                                    }
                                    size="4"
                                    color="gray-400"
                                    iconClassName="flex items-center justify-center -mt-0.5"
                                  />
                                </div>
                                <SwitchOnOff
                                  checked={
                                    urlButtonLink2?.includes("{{")
                                      ? false
                                      : (selectedNode?.data?.urlButton2
                                          ?.trackingEnabled ?? true)
                                  }
                                  onChange={(enabled) => {
                                    if (
                                      isInPanel &&
                                      !urlButtonLink2?.includes("{{")
                                    ) {
                                      setModifications(true);
                                      handleMessageChange(
                                        "urlButton2",
                                        {
                                          text: urlButton2,
                                          url: urlButtonLink2,
                                          trackingEnabled: enabled,
                                        },
                                        nodes,
                                        setNodes,
                                        selectedNode,
                                      );
                                    }
                                  }}
                                  color="dark/zinc"
                                  disabled={urlButtonLink2?.includes("{{")}
                                />
                              </div>
                            </Field>
                          )}

                          <div className="flex flex-row mb-2">
                            <InputGroup>
                              <ArrowTopRightOnSquareIcon />
                              <Input
                                value={urlButton2}
                                placeholder="Link title"
                                onChange={(e) => {
                                  setUrlButton2(e.target.value);
                                  if (isInPanel) {
                                    setModifications(true);
                                    handleMessageChange(
                                      "urlButton2",
                                      {
                                        text: e.target.value,
                                        url: urlButtonLink2,
                                        trackingEnabled:
                                          selectedNode?.data?.urlButton2
                                            ?.trackingEnabled ?? true,
                                      },
                                      nodes,
                                      setNodes,
                                      selectedNode,
                                    );
                                  }
                                }}
                                onBlur={(e) =>
                                  setUrlButton2ErrorMsg(
                                    formChecks.checkUrlButton(
                                      e.target.value,
                                      urlButtonLink2,
                                    ),
                                  )
                                }
                                invalid={!!urlButton2ErrorMsg}
                                maxLength={25}
                                showCharacterCount
                                autoComplete="off"
                              />
                            </InputGroup>
                            <Button
                              color="white"
                              className="border ml-2"
                              onClick={() => {
                                setUrlButton2("");
                                setUrlButtonLink2("");
                                setUrlButton2ErrorMsg("");
                                setUrlButtonLink2ErrorMsg("");
                                setNumberOfUrl((n) => n - 1);
                                if (isInPanel) {
                                  setModifications(true);
                                  handleMessageChange(
                                    "urlButton2",
                                    null,
                                    nodes,
                                    setNodes,
                                    selectedNode,
                                  );
                                  removeEdgeOnUrlButton(1);
                                }
                              }}
                            >
                              <TrashIcon />
                            </Button>
                          </div>
                          <Input
                            value={urlButtonLink2?.replace(
                              /\{\{[^}]+\}\}/g,
                              "{{url_variable}}",
                            )}
                            ref={urlButtonLink2Ref}
                            placeholder="Link URL"
                            onChange={(e) => {
                              setUrlButtonLink2(
                                e.target.value.replace(
                                  /\{\{[^}]+\}\}/g,
                                  "{{url_variable}}",
                                ),
                              );
                              if (isInPanel) {
                                setModifications(true);
                                handleMessageChange(
                                  "urlButton2",
                                  {
                                    text: urlButton2,
                                    url: e.target.value,
                                    trackingEnabled:
                                      selectedNode?.data?.urlButton2
                                        ?.trackingEnabled ?? true,
                                  },
                                  nodes,
                                  setNodes,
                                  selectedNode,
                                );
                              }
                            }}
                            onBlur={(e) =>
                              setUrlButtonLink2ErrorMsg(
                                formChecks.checkUrlButtonLink(
                                  false,
                                  e.target.value,
                                  urlButton2,
                                ),
                              )
                            }
                            invalid={!!urlButtonLink2ErrorMsg}
                            autoComplete="off"
                          />
                          {urlButton2ErrorMsg && (
                            <Field>
                              <ErrorMessage>{urlButton2ErrorMsg}</ErrorMessage>
                            </Field>
                          )}
                          {urlButtonLink2ErrorMsg && (
                            <Field>
                              <ErrorMessage>
                                {urlButtonLink2ErrorMsg}
                              </ErrorMessage>
                            </Field>
                          )}
                          {urlVariableNotAtEnd2 && (
                            <ErrorMessage>
                              {"{{url_variable}}"} must be at the end of the URL
                            </ErrorMessage>
                          )}
                          <Button
                            className="mt-2"
                            color="secondaryButton"
                            onClick={() => {
                              addUrlVariable(urlButtonLink2Ref, 2);
                            }}
                            disabled={
                              urlButtonLink2?.includes("{{url_variable}}") ||
                              urlButtonLink2?.includes("{{1}}")
                            }
                          >
                            Add url variable
                          </Button>
                        </>
                      )}
                      {/* The buttons order is forced for LTO WA Template (Copy code [optional] > URL > QR)*/}
                      <br />
                    </>
                  )}

                  {!isLimitedTimeOffer && numberOfUrl < 2 && (
                    <>
                      <Button
                        color="secondaryButton"
                        onClick={() => {
                          if (
                            campaign?.automationType === "abandoned-cart" &&
                            shop
                          ) {
                            setUrlButton("Je récupère mon panier");
                            setUrlButtonLink(
                              `https://${shop?.domain}/${shop?.id}/checkouts/{{1}}`,
                            );
                            if (isInPanel) {
                              setModifications(true);
                              handleMessageChange(
                                "urlButton",
                                {
                                  text: "Je récupère mon panier",
                                  url: `https://${shop?.domain}/${shop?.id}/checkouts/{{1}}`,
                                },
                                nodes,
                                setNodes,
                                selectedNode,
                              );
                            }
                          } else if (
                            campaign?.automationType === "back-in-stock" &&
                            shop
                          ) {
                            setUrlButton("Voir le produit");
                            setUrlButtonLink(
                              `https://${shop?.domain}/products/{{1}}`,
                            );
                            if (isInPanel) {
                              handleMessageChange(
                                "urlButton",
                                {
                                  text: "Voir le produit",
                                  url: `https://${shop?.domain}/products/{{1}}`,
                                },
                                nodes,
                                setNodes,
                                selectedNode,
                              );
                            }
                          } else if (
                            campaign?.automationType ===
                            "just-abandoned-checkout"
                          ) {
                            setUrlButton("Je récupère mon panier");
                            setUrlButtonLink(
                              "https://checkout-form.getjust.eu/{{1}}",
                            );
                            if (isInPanel) {
                              handleMessageChange(
                                "urlButton",
                                {
                                  text: "Je récupère mon panier",
                                  url: "https://checkout-form.getjust.eu/{{1}}",
                                },
                                nodes,
                                setNodes,
                                selectedNode,
                              );
                            }
                          }
                          setNumberOfUrl((n) => n + 1);
                        }}
                      >
                        Add link
                      </Button>
                      <br />
                    </>
                  )}
                  {!isLimitedTimeOffer && numberOfUrl >= 1 && (
                    <div className="flex justify-between">
                      <Legend>Position</Legend>
                      <div className="flex mt-3">
                        <Text className="mr-1">Top</Text>
                        <Switch
                          checked={isUrlButtonAtBottom}
                          onChange={(checked) => {
                            setIsUrlButtonAtBottom(checked);
                            if (isInPanel) {
                              setModifications(true);
                              handleMessageChange(
                                "isUrlButtonAtBottom",
                                checked,
                                nodes,
                                setNodes,
                                selectedNode,
                              );
                            }
                          }}
                        />
                        <Text className="ml-1">Bottom</Text>
                      </div>
                    </div>
                  )}
                </Field>
              </Fieldset>
            </div>
          )}
          {cards && Array.isArray(cards) && (
            <>
              <Divider className="mt-5 mb-3" />
              <CarouselForm
                api={api}
                user={user}
                isInPanel={isInPanel}
                selectedNode={selectedNode}
                nodes={nodes}
                setNodes={setNodes}
                edges={edges}
                setEdges={setEdges}
                selectedCardsHeader={selectedCardsHeader}
                setSelectedCardsHeader={setSelectedCardsHeader}
                cards={cards}
                setCards={setCards}
                isUploading={isUploading}
                setIsUploading={setIsUploading}
                paramOptions={paramOptions}
                addVariable={addVariable}
                buttonCardRefs={buttonCardRefs}
                bodyCardRefs={bodyCardRefs}
                parameterToInsert={parameterToInsert}
                setParameterToInsert={setParameterToInsert}
                updateBodyCardParameters={updateBodyCardParameters}
                deleteEdge={deleteEdge}
                setModifications={setModifications}
                carouselCardIndex={carouselCardIndex}
                setCarouselCardIndex={setCarouselCardIndex}
                carouselComponentIndex={carouselComponentIndex}
                setCarouselComponentIndex={setCarouselComponentIndex}
                setFile={setFile}
                setIsPanelOpened={setIsPanelOpened}
                setIsConvertCompressDialogOpen={setIsConvertCompressDialogOpen}
                selectedNodeConvertCompress={selectedNodeConvertCompress}
                setSelectedNodeConvertCompress={setSelectedNodeConvertCompress}
                bodyCardErrors={bodyCardErrors}
                setBodyCardErrors={setBodyCardErrors}
                buttonCardErrors={buttonCardErrors}
                setButtonCardErrors={setButtonCardErrors}
              />
            </>
          )}
        </main>

        <aside className={previewClasses}>
          <div className={previewMargin}>
            <Field>
              <Legend>Preview</Legend>
              <TemplatePreview
                selectedHeader={selectedHeader[0]}
                limitedTimeOffer={limitedTimeOffer}
                isLimitedTimeOffer={isLimitedTimeOffer}
                body={body}
                footer={footer}
                flowButton={flowButton}
                copyCodeButton={copyCodeButton}
                quickReply1={quickReply1}
                quickReply2={quickReply2}
                quickReply3={quickReply3}
                urlButton={urlButton}
                urlButton2={urlButton2}
                width="100%"
                imageMaxHeight="100%"
                isUrlButtonAtBottom={isUrlButtonAtBottom}
                imageUrl={
                  headerImageLink !== ""
                    ? headerImageLink
                    : selectedNode?.data?.headerParameters?.image ===
                        "shopifyProductImage"
                      ? "https://storage.gadget.dev/files/22129/42940/media/file/O68TikY4GnkGUAzFNnGnB/c9168318-968c-4199-80dc-64da075a0aba"
                      : selectedNode?.data?.headerParameters?.image?.link
                }
                videoUrl={headerVideoLink}
                isInPanel={isInPanel}
                cards={cards}
              />
            </Field>
          </div>
        </aside>
      </div>
    </>
  );
}
